import { useMemo } from 'react'
import type { Section } from '@/src/renderer/lib/db/models'
import { useOptimisticEntityOverrides } from './useOptimisticEntityOverrides'

/**
 * Section-typed wrapper around `useOptimisticEntityOverrides`. Used by
 * ProjectPage / QuickNotesPage to mirror collapse / rename / delete writes
 * synchronously so the UI flips in the next paint instead of waiting on
 * the IDB write → LiveQuery → re-render roundtrip.
 *
 * See `useOptimisticEntityOverrides` for cleanup semantics.
 */
export function useOptimisticSectionOverrides(rawSections: Section[] | undefined): {
  optimisticSectionOverrides: Map<string, Partial<Section>>
  setOptimisticSectionOverride: (sectionId: string, partial: Partial<Section>) => void
  applySectionOverride: (section: Section) => Section
} {
  const { optimisticOverrides, setOverride, apply } = useOptimisticEntityOverrides<Section>(rawSections)
  return useMemo(() => ({
    optimisticSectionOverrides: optimisticOverrides,
    setOptimisticSectionOverride: setOverride,
    applySectionOverride: apply,
  }), [optimisticOverrides, setOverride, apply])
}
